import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import Button from './Button';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  variant = 'danger',
  /** Same stacking as Modal — use 2+ when opening over a modal with stackPriority 1 */
  stackPriority = 0,
  closeOnConfirm = true,
}) => {
  if (!isOpen) return null;

  const overlayZ = 9998 + stackPriority * 2;
  const panelZ = 9999 + stackPriority * 2;

  const handleConfirm = () => {
    onConfirm?.();
    if (closeOnConfirm) {
      onClose?.();
    }
  };

  const dialogContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px]"
            style={{ zIndex: overlayZ }}
          />

          {/* Dialog */}
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: panelZ }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="relative w-full max-w-md"
            >
              <div className="relative overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)]">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-500 via-red-600 to-red-500" />
                
                {/* Close Button */}
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-xl hover:bg-gray-100 transition-colors z-10"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </motion.button>

                {/* Icon */}
                <div className="flex justify-center pt-8 pb-4">
                  <div
                    className={`rounded-2xl p-4 ${
                      variant === 'danger' ? 'bg-red-50 ring-1 ring-red-100' : 'bg-amber-50 ring-1 ring-amber-100'
                    }`}
                  >
                    <AlertTriangle
                      className={`h-10 w-10 ${variant === 'danger' ? 'text-red-600' : 'text-amber-600'}`}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="px-8 pb-8 text-center relative">
                  <h3 className="mb-3 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                    {title}
                  </h3>
                  <p className="mb-6 whitespace-pre-line text-sm leading-relaxed text-gray-600 sm:text-base">
                    {message}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="secondary"
                      onClick={onClose}
                      className="flex-1"
                    >
                      {cancelText}
                    </Button>
                    <Button
                      variant={variant}
                      onClick={handleConfirm}
                      className="flex-1"
                    >
                      {confirmText}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(dialogContent, document.body);
};

export default ConfirmDialog;


